import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BookOpen,
  FileText,
  Video,
  Headphones,
  ExternalLink,
  Star,
  Crown
} from "lucide-react";

export default function SelfDevelopmentPage() {
  const hogPicks = [
    {
      title: "The OZ Principle",
      author: "Roger Connors, Tom Smith & Craig Hickman",
      description: "Getting results through individual and organizational accountability. A must-read for understanding how to take ownership and drive results.",
      category: "Accountability & Leadership",
      link: "https://www.amazon.co.uk/s?k=The+Oz+Principle+Roger+Connors"
    },
    {
      title: "Measure What Matters",
      author: "John Doerr",
      description: "How Google, Bono, and the Gates Foundation rock the world with OKRs. Learn how to set goals that drive real results.",
      category: "Goals & Performance",
      link: "https://www.amazon.co.uk/s?k=Measure+What+Matters+John+Doerr"
    }
  ];

  const books = [
    {
      title: "A Season in Dornoch",
      author: "Lorne Rubenstein",
      description: "The classic book about spending a summer in Dornoch - golf, local life, and the Scottish Highlands. Essential reading for anyone at Royal Dornoch.",
      category: "Royal Dornoch",
      link: "https://www.amazon.co.uk/s?k=A+Season+in+Dornoch+Lorne+Rubenstein"
    },
    {
      title: "Unreasonable Hospitality",
      author: "Will Guidara",
      description: "The remarkable power of giving people more than they expect. From the team behind Eleven Madison Park - essential reading for anyone in service.",
      category: "Hospitality",
      link: "https://www.amazon.co.uk/s?k=Unreasonable+Hospitality+Will+Guidara"
    },
    {
      title: "Setting the Table",
      author: "Danny Meyer",
      description: "The transforming power of hospitality in business. Danny Meyer's philosophy on 'enlightened hospitality' that built a restaurant empire.",
      category: "Hospitality",
      link: "https://www.amazon.co.uk/s?k=Setting+the+Table+Danny+Meyer"
    },
    {
      title: "Harvey Penick's Little Red Book",
      author: "Harvey Penick",
      description: "Timeless wisdom on golf and life from one of the game's greatest teachers. Simple, practical advice that applies on and off the course.",
      category: "Golf Classic",
      link: "https://www.amazon.co.uk/s?k=Harvey+Penick+Little+Red+Book"
    },
    {
      title: "The New Gold Standard",
      author: "Joseph Michelli",
      description: "5 leadership principles for creating a legendary customer experience - lessons from The Ritz-Carlton Hotel Company.",
      category: "Service Excellence",
      link: "https://www.amazon.co.uk/s?k=The+New+Gold+Standard+Ritz+Carlton"
    },
    {
      title: "Be Our Guest",
      author: "Disney Institute",
      description: "Perfecting the art of customer service. Disney's approach to exceeding expectations - applicable to any industry.",
      category: "Customer Service",
      link: "https://www.amazon.co.uk/s?k=Be+Our+Guest+Disney+Institute"
    }
  ];

  const articles = [
    {
      title: "Pace of Play Resource Center",
      source: "USGA",
      description: "Official USGA guidelines on managing pace of play - 40 second rule, ready golf, and more.",
      link: "https://www.usga.org/pace-of-play-resource-center.html"
    },
    {
      title: "Body Language in Customer Service",
      source: "HubSpot",
      description: "How posture, eye contact, and non-verbal cues impact guest interactions.",
      link: "https://blog.hubspot.com/service/body-language-in-customer-service"
    },
    {
      title: "The Best Golf Books - What To Read",
      source: "The Fried Egg",
      description: "Curated reading list of essential golf books on course design, history, and the game itself.",
      link: "https://www.thefriedegg.com/articles/golf-books-what-to-read-and-what-im-reading-next/"
    },
    {
      title: "Tips for Improving Pace of Play",
      source: "USGA",
      description: "Practical suggestions for speeding up play without compromising the experience.",
      link: "https://www.usga.org/pace-of-play-resource-center/tips-and-suggestions-for-improving-pace-of-play.html"
    }
  ];

  const videos = [
    {
      title: "The Happy Secret to Better Work",
      speaker: "Shawn Achor",
      platform: "TED Talk",
      duration: "12 mins",
      description: "How positive psychology can improve productivity and service.",
      link: "https://www.ted.com/talks/shawn_achor_the_happy_secret_to_better_work"
    },
    {
      title: "How Great Leaders Inspire Action",
      speaker: "Simon Sinek",
      platform: "TED Talk",
      duration: "18 mins",
      description: "Start with 'Why' - understanding purpose in what we do.",
      link: "https://www.ted.com/talks/simon_sinek_how_great_leaders_inspire_action"
    },
    {
      title: "The Puzzle of Motivation",
      speaker: "Dan Pink",
      platform: "TED Talk",
      duration: "18 mins",
      description: "What really motivates us at work - autonomy, mastery, purpose.",
      link: "https://www.ted.com/talks/dan_pink_the_puzzle_of_motivation"
    }
  ];

  const podcasts = [
    {
      title: "The Golf Podcast",
      host: "Various",
      description: "Insights into the golf industry, operations, and trends.",
      link: "https://podcasts.apple.com/gb/podcast/the-golf-podcast/id1447073868"
    },
    {
      title: "No Laying Up",
      host: "NLU Team",
      description: "Golf course architecture, travel, and industry insights from passionate golfers.",
      link: "https://nolayingup.com/podcasts/"
    },
    {
      title: "The Look In Podcast",
      host: "Scottish Golf",
      description: "Scottish golf news, interviews, and industry insights.",
      link: "https://www.scottishgolf.org/"
    }
  ];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-8 animate-fade-in-up">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
            Self Development
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Expand your knowledge and grow your career with these recommended resources.
            <span className="block text-xs mt-1 italic">All links open in a new tab — your place here is saved.</span>
          </p>
        </div>

        {/* HOG's Top Picks */}
        <Card className="border-[var(--rd-gold)] bg-gradient-to-br from-[var(--rd-gold)]/5 to-transparent">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[var(--rd-gold)]/20 flex items-center justify-center">
                <Crown className="w-5 h-5 text-[var(--rd-gold)]" />
              </div>
              <div>
                <span className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                  Head of Golf's Top Picks
                </span>
                <p className="text-sm font-normal text-[hsl(var(--muted-foreground))]">
                  Essential reading recommended by Scott
                </p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {hogPicks.map((book) => (
                <div 
                  key={book.title}
                  className="p-4 bg-white rounded-sm border border-[var(--rd-gold)]/30 hover:border-[var(--rd-gold)] transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-sm bg-[var(--rd-gold)]/10 flex items-center justify-center flex-shrink-0">
                      <Star className="w-5 h-5 text-[var(--rd-gold)]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-[var(--rd-green)]">{book.title}</h4>
                      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">by {book.author}</p>
                      <span className="inline-block text-xs bg-[var(--rd-gold)]/10 text-[var(--rd-gold)] px-2 py-0.5 rounded mb-2">
                        {book.category}
                      </span>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">{book.description}</p>
                      <a 
                        href={book.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[var(--rd-green)] hover:underline mt-2"
                      >
                        View on Amazon <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Books Section */}
        <Card className="border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[var(--rd-green)]/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[var(--rd-green)]" />
              </div>
              <span className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                Recommended Books
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {books.map((book) => (
                <div 
                  key={book.title}
                  className="p-4 border border-[hsl(var(--border))] rounded-sm hover:border-[var(--rd-green)]/50 transition-colors"
                >
                  <span className="inline-block text-xs bg-[var(--rd-green)]/10 text-[var(--rd-green)] px-2 py-0.5 rounded mb-2">
                    {book.category}
                  </span>
                  <h4 className="font-semibold text-[var(--rd-green)] mb-1">{book.title}</h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">by {book.author}</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{book.description}</p>
                  <a 
                    href={book.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[var(--rd-green)] hover:underline"
                  >
                    View Book <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Articles Section */}
        <Card className="border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-blue-500/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                Articles & Reading
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {articles.map((article) => (
                <a 
                  key={article.title}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 border border-[hsl(var(--border))] rounded-sm hover:border-blue-500/50 hover:bg-blue-50/30 transition-colors"
                >
                  <div className="flex-1">
                    <h4 className="font-semibold text-[var(--rd-green)]">{article.title}</h4>
                    <p className="text-sm text-blue-600 mb-1">{article.source}</p>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{article.description}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-[hsl(var(--muted-foreground))] flex-shrink-0 mt-1" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Videos Section */}
        <Card className="border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-red-500/10 flex items-center justify-center">
                <Video className="w-5 h-5 text-red-500" />
              </div>
              <span className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                Videos & TED Talks
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {videos.map((video) => (
                <a 
                  key={video.title}
                  href={video.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 border border-[hsl(var(--border))] rounded-sm hover:border-red-500/50 hover:bg-red-50/30 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs bg-red-500/10 text-red-600 px-2 py-0.5 rounded">
                      {video.platform}
                    </span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      {video.duration}
                    </span>
                  </div>
                  <h4 className="font-semibold text-[var(--rd-green)] mb-1">{video.title}</h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{video.speaker}</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">{video.description}</p>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Podcasts Section */}
        <Card className="border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-purple-500/10 flex items-center justify-center">
                <Headphones className="w-5 h-5 text-purple-500" />
              </div>
              <span className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                Podcasts
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {podcasts.map((podcast) => (
                <a 
                  key={podcast.title}
                  href={podcast.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-4 border border-[hsl(var(--border))] rounded-sm hover:border-purple-500/50 hover:bg-purple-50/30 transition-colors"
                >
                  <h4 className="font-semibold text-[var(--rd-green)] mb-1">{podcast.title}</h4>
                  <p className="text-sm text-purple-600 mb-2">Hosted by {podcast.host}</p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">{podcast.description}</p>
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Footer Note */}
        <Card className="border-[hsl(var(--border))]">
          <CardContent className="p-6 text-center">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Have a recommendation? Speak to your Shift Leader or Scott to have it added.
              <br />
              <span className="text-[var(--rd-green)]">Continuous learning is part of the Royal Dornoch standard.</span>
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
